import { Fragment, type Ref } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckRounded from '@mui/icons-material/CheckRounded';

export interface WizardStepperProps {
  /** Ordered step labels (translated copy). */
  steps: string[];
  /** Index of the active step; earlier steps render as completed. */
  current: number;
  /**
   * Fired when a **completed** step is clicked (to jump back). Future steps are
   * never clickable — the form gates forward progress through validation.
   */
  onStepClick?: (index: number) => void;
  /**
   * Caps each connector rail in px and centers the whole track. Without it the
   * rails are pure `flex: 1`, which is right inside a width-capped form but
   * stretches the steps to the far edges of a full-width one. Omit to keep the
   * stretch-to-fill behavior.
   */
  connectorMax?: number;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Horizontal wizard progress (the Polaris kit `WizardStepper`) — numbered nodes
 * with the label **beside** each node, joined by connector rails. Distinct from
 * {@link import('./WorkflowStepper').WorkflowStepper} (the vertical *lifecycle*
 * timeline): this is the *form* step tracker for the device-acquisition wizard.
 * Completed nodes are filled + checkable (jump back); the active node is the
 * accent ring; future nodes are muted and inert.
 *
 * <p>
 * <b>The rails are siblings of the steps, not children.</b> A flex item only
 * resolves a `flex-basis` against a *definite* container width; nesting a rail
 * inside a content-sized per-step wrapper made it resolve against an indefinite
 * one and collapse to `min-width`, so `connectorMax` had no visible effect. Both
 * live directly under the full-width track instead, which is also what lets
 * `justify-content` collect the slack a capped rail frees.
 * </p>
 */
export function WizardStepper({
  steps,
  current,
  onStepClick,
  connectorMax,
  ref,
}: WizardStepperProps) {
  const theme = useTheme();
  const accent = theme.palette.secondary.main;
  const success = theme.palette.success.main;

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        justifyContent: connectorMax ? 'center' : undefined,
      }}
      role="list"
      aria-label={steps.join(' → ')}
    >
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        const clickable = done && !!onStepClick;
        const nodeColor = done ? success : active ? accent : theme.palette.divider;
        const last = i === steps.length - 1;

        return (
          <Fragment key={label}>
            <Box
              role="listitem"
              sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}
            >
              <Box
                component={clickable ? 'button' : 'div'}
                type={clickable ? 'button' : undefined}
                onClick={clickable ? () => onStepClick(i) : undefined}
                aria-current={active ? 'step' : undefined}
                sx={{
                  width: 28,
                  height: 28,
                  p: 0,
                  flexShrink: 0,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  cursor: clickable ? 'pointer' : 'default',
                  bgcolor: done ? nodeColor : active ? alpha(accent, 0.14) : 'background.paper',
                  border: `2px solid ${nodeColor}`,
                  color: done ? '#fff' : active ? accent : 'text.secondary',
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {done ? <CheckRounded sx={{ fontSize: 15 }} /> : i + 1}
              </Box>
              <Typography
                sx={{
                  fontSize: 13,
                  lineHeight: 1.2,
                  fontWeight: active ? 700 : 500,
                  color: done || active ? 'text.primary' : 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Typography>
            </Box>
            {!last && (
              <Box
                aria-hidden
                sx={{
                  flex: 1,
                  height: '2px',
                  mx: 1.5,
                  minWidth: 16,
                  maxWidth: connectorMax,
                  borderRadius: '1px',
                  bgcolor: done ? success : theme.palette.divider,
                }}
              />
            )}
          </Fragment>
        );
      })}
    </Box>
  );
}
